import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	InputAdornment,
	IconButton,
	Tooltip,
	Chip,
	Stack,
	alpha,
	CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Page from "../../components/Page";
import axios from "../../utils/axios";

const statusColorMap = {
	PRODUCTION: "success",
	SUSPENDED: "error",
	PAUSED: "warning",
	ONBOARDING: "info",
	EVALUATING: "default",
	DATA_ACTIVE: "primary",
	DATA_INACTIVE: "secondary",
	CAMPAIGN_ACTIVE: "success",
	CAMPAIGN_INACTIVE: "default",
};

export default function PracticeList() {
	const navigate = useNavigate();
	const [practices, setPractices] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchQuery, setSearchQuery] = useState("");

	useEffect(() => {
		const fetchPractices = async () => {
			try {
				const response = await axios.get("/campaign/getAllPractices");
                console.log(response.data.data);
				if (response.data.data) {
					setPractices(response.data.data);
				}
			} catch (err) {
				console.error("Error fetching practices:", err);
				setError("Failed to load practices");
			} finally {
				setLoading(false);
			}
		};
		fetchPractices();
	}, []);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const filteredPractices = useMemo(() => {
		if (!Array.isArray(practices)) return [];
		return practices.filter((practice) =>
			practice.name.toLowerCase().includes(searchQuery.toLowerCase())
		);
	}, [practices, searchQuery]);

	const paginatedPractices = useMemo(() => {
		return filteredPractices.slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage
		);
	}, [filteredPractices, page, rowsPerPage]);

	if (loading) {
		return (
			<Page>
				<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
					<CircularProgress />
				</Box>
			</Page>
		);
	}

	if (error) {
		return (
			<Page>
				<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
					<Typography color="error">{error}</Typography>
				</Box>
			</Page>
		);
	}

	return (
		<Page>
			<Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, px: 2 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					sx={{ mb: 3 }}
				>
					<Typography
						variant="h4"
						sx={{ fontWeight: "bold", color: "text.primary" }}
					>
						Practices
					</Typography>
					<TextField
						size="small"
						placeholder="Search practices..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						sx={{
							width: 300,
							"& .MuiOutlinedInput-root": {
								bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
								"&:hover": {
									bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
								},
							},
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="primary" />
								</InputAdornment>
							),
						}}
					/>
				</Stack>

				<Paper
					sx={{
						width: "100%",
						overflow: "hidden",
						bgcolor: "background.paper",
						boxShadow: (theme) => theme.customShadows.z8,
					}}
				>
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell
										sx={{ fontWeight: "bold", color: "text.secondary" }}
									>
										Practice Name
									</TableCell>
									<TableCell
										sx={{ fontWeight: "bold", color: "text.secondary" }}
									>
										Status
									</TableCell>
									<TableCell
										sx={{ fontWeight: "bold", color: "text.secondary" }}
									>
										Last Transaction
									</TableCell>
									<TableCell
										align="right"
										sx={{ fontWeight: "bold", color: "text.secondary" }}
									>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedPractices.map((practice) => (
									<TableRow
										key={practice.id}
										hover
										sx={{
											"&:hover": {
												bgcolor: (theme) =>
													alpha(theme.palette.primary.main, 0.04),
											},
										}}
									>
										<TableCell>
											<Typography variant="body1" sx={{ fontWeight: "medium" }}>
												{practice.name}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={practice.status}
												color={statusColorMap[practice.status]}
												variant="outlined"
												size="small"
												sx={{
													borderColor: (theme) =>
														alpha(
															theme.palette[statusColorMap[practice.status]]
																.main,
															0.48
														),
													color: (theme) =>
														theme.palette[statusColorMap[practice.status]].main,
												}}
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body1" color="text.primary">
												{new Date(
													practice.lastTransactionDate
												).toLocaleDateString()}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Tooltip title="View Details">
												<IconButton
													onClick={() =>
														navigate(`/dashboard/practices/${practice.practiceId}`)
													}
													sx={{
														bgcolor: (theme) =>
															alpha(theme.palette.primary.main, 0.08),
														"&:hover": {
															bgcolor: (theme) =>
																alpha(theme.palette.primary.main, 0.16),
														},
													}}
												>
													<VisibilityIcon color="primary" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={filteredPractices.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						sx={{
							"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
								{
									color: "text.secondary",
								},
						}}
					/>
				</Paper>
			</Box>
		</Page>
	);
}
